/**
 * Gym Shorts v4.1 — Pack selector behavior.
 *
 * Custom element <gs-v4-1-pack-selector>:
 *   - Click on a [data-pack] tile selects that pack.
 *   - Dispatches the SAME gs-v4:pack-change event v4 uses, so the design picker
 *     (assets/gym-shorts-v4.js) reacts to budget changes unchanged.
 *   - Updates the Showtime headline price block to reflect the selected pack
 *     (.#price-item.@regular + .@compare). Headline stays bound to the pack;
 *     it does NOT bump on tee additions (those are add-ons, handled at cart).
 *
 * No coordinator logic here — gs-v4-coordinator from gym-shorts-v4.js handles
 * cart submission for both v4 and v4.1.
 */
(() => {
  const REG_SEL = '.\\#price-item.\\@regular';
  const CMP_SEL = '.\\#price-item.\\@compare';

  class GsV41PackSelector extends HTMLElement {
    connectedCallback() {
      this._onClick = (e) => {
        const tile = e.target.closest('[data-pack]');
        if (!tile || !this.contains(tile)) return;
        const n = Number(tile.dataset.pack);
        if (!Number.isFinite(n)) return;
        if (n === this.selected) return;
        this.select(n);
      };
      this.addEventListener('click', this._onClick);
      // Sync headline price to the default pack on first paint.
      const initial = this.querySelector(`[data-pack="${this.selected}"]`);
      if (initial) this._applyHeadline(initial);
    }
    disconnectedCallback() {
      if (this._onClick) this.removeEventListener('click', this._onClick);
    }
    /** Exposed for gs-v4-coordinator (matches v4 pack-selector contract). */
    get selected() { return Number(this.dataset.selectedPack); }

    select(n) {
      this.dataset.selectedPack = String(n);
      let chosen = null;
      this.querySelectorAll('[data-pack]').forEach((t) => {
        const active = Number(t.dataset.pack) === n;
        t.classList.toggle('is-selected', active);
        t.setAttribute('aria-checked', String(active));
        if (active) chosen = t;
      });
      if (chosen) this._applyHeadline(chosen);
      document.dispatchEvent(
        new CustomEvent('gs-v4:pack-change', { detail: { pack: n } })
      );
    }

    _applyHeadline(tile) {
      const price = tile.dataset.packPrice || '';
      const compare = tile.dataset.packCompare || '';
      const priceCents = Number(tile.dataset.packPriceCents || 0);
      const compareCents = Number(tile.dataset.packCompareCents || 0);
      const hasCompare = compareCents > priceCents && compareCents > 0;

      const reg = document.querySelector(REG_SEL);
      if (reg) reg.textContent = price;

      let cmp = document.querySelector(CMP_SEL);
      if (!cmp && hasCompare && reg) {
        // Create a compare element next to the regular price if one doesn't
        // exist (products without compare_at_price won't have it server-
        // rendered). Use <s> so the browser's default strikethrough applies
        // regardless of theme CSS.
        cmp = document.createElement('s');
        cmp.setAttribute('class', '#price-item @compare');
        reg.parentNode.insertBefore(cmp, reg.nextSibling);
      }
      if (cmp) {
        if (hasCompare) {
          cmp.textContent = compare;
          cmp.removeAttribute('hidden');
          cmp.style.display = '';
          // Force strikethrough even if a pre-existing element lacked it.
          cmp.style.textDecoration = 'line-through';
        } else {
          // Clear the text too so DOM inspectors / screen readers don't see
          // stale values from a previously selected pack with a compare price.
          cmp.textContent = '';
          cmp.setAttribute('hidden', '');
          cmp.style.display = 'none';
        }
      }
    }
  }

  if (!customElements.get('gs-v4-1-pack-selector')) {
    customElements.define('gs-v4-1-pack-selector', GsV41PackSelector);
  }
})();
